import { type Edge } from '@xyflow/react';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';

import { type Dimension } from '../approvalWorkflow';
import { ROOT_NODE_ID } from '../helpers/initialNodes';
import { type CustomNode } from '../node';

// Helper branches are the edges and nodes that have action to add new nodes
export const addHelperBranches = ({
  nodes,
  edges,
  t,
  getDimensionValues,
}: {
  nodes: CustomNode[];
  edges: Edge[];
  t: TGlobalFunctionTyped;
  getDimensionValues: (dimensionType: Dimension['type']) => string[];
}) => {
  // We should always filter out existing "newConditionNode" to clean the orphaned nodes and edges
  const realNodes = nodes.filter((node) => node.type !== 'newConditionNode');
  const realEdges = edges.filter(
    (edge) => !edge.id.startsWith('newConditionEdge-'),
  );

  const eligibleNodes = realNodes.filter((node) => {
    const hasChildren = realEdges.some((edge) => edge.source === node.id);
    const childrenDimensionHasAllPossibleValuesAreSelected =
      getChildrenDimensionHasAllPossibleValuesAreSelected(
        node.id,
        nodes,
        edges,
        getDimensionValues,
      );

    return (
      (node.id === ROOT_NODE_ID || node.type === 'baseNode') &&
      hasChildren &&
      !childrenDimensionHasAllPossibleValuesAreSelected
    );
  });

  const newConditionNodes: CustomNode[] = eligibleNodes.map((node) => ({
    id: `newConditionNode-${node.id}`,
    type: 'newConditionNode',
    data: {
      nodeType: 'newConditionNode',
      parentId: node.id,
    },
    position: { x: 0, y: 0 },
  }));

  const newConditionEdges: Edge[] = newConditionNodes.map(
    (newConditionNode) => {
      const targetId = newConditionNode.id;
      const sourceId =
        newConditionNode.type === 'newConditionNode'
          ? newConditionNode.data.parentId
          : '';

      return {
        id: `newConditionEdge-${sourceId}-${targetId}`,
        type: 'labelEdge',
        source: sourceId,
        target: targetId,
        data: {
          labelText: t('approvalWorkflows.labelEdge.if'),
          labelVariant: 'purple',
        },
      };
    },
  );

  return {
    nodes: [...realNodes, ...newConditionNodes],
    edges: [...realEdges, ...newConditionEdges],
  };
};

export const moveNewConditionNodesAtTheStart = (nodes: CustomNode[]) => {
  const newConditionNodes = nodes.filter(
    (node) => node.type === 'newConditionNode',
  );
  const otherNodes = nodes.filter((node) => node.type !== 'newConditionNode');

  return [...newConditionNodes, ...otherNodes];
};

export const getNodeLevel = (nodeId: string, edges: Edge[]): number => {
  const targettingEdge = edges.find((edge) => edge.target === nodeId);
  return targettingEdge ? 1 + getNodeLevel(targettingEdge.source, edges) : 0;
};

export const findDescendantNodes = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
): string[] => {
  const childEdges = edges.filter((edge) => edge.source === nodeId);
  const childNodeIds = childEdges.map((edge) => edge.target);
  const allDescendantNodeIds = childNodeIds.flatMap((childNodeId) =>
    findDescendantNodes(childNodeId, nodes, edges),
  );
  return [nodeId, ...allDescendantNodeIds];
};

export const calculateSiblingNodes = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
): CustomNode[] => {
  const sourceEdge = edges.find((edge) => edge.target === nodeId);
  if (!sourceEdge) {
    return [];
  }

  const siblingNodeIds = edges
    .filter((edge) => edge.source === sourceEdge.source)
    .map((edge) => edge.target);

  return siblingNodeIds
    .map((siblingNodeId) =>
      nodes.find(
        (node) => node.type === 'baseNode' && node.id === siblingNodeId,
      ),
    )
    .filter((node): node is CustomNode => node !== undefined);
};

export const calculateChildNodes = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
): CustomNode[] => {
  const childEdges = edges.filter((edge) => edge.source === nodeId);
  const childNodeIds = childEdges.map((edge) => edge.target);
  return childNodeIds
    .map((childNodeId) => nodes.find((node) => node.id === childNodeId))
    .filter((node): node is CustomNode => node !== undefined);
};

export const calculateParentNode = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
): CustomNode | null => {
  const sourceEdge = edges.find((edge) => edge.target === nodeId);
  if (!sourceEdge) {
    return null;
  }
  return nodes.find((node) => node.id === sourceEdge.source) ?? null;
};

export const calculateGetNodeById = (nodeId: string, nodes: CustomNode[]) => {
  return nodes.find((node) => node.id === nodeId);
};

const getChildrenValues = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
) => {
  const childNodes = calculateChildNodes(nodeId, nodes, edges);
  return childNodes
    .filter(
      (node): node is CustomNode & { type: 'baseNode' } =>
        node.type === 'baseNode',
    )
    .flatMap((node) => node.data.values);
};

const getChildrenDimensionHasAllPossibleValuesAreSelected = (
  nodeId: string,
  nodes: CustomNode[],
  edges: Edge[],
  getDimensionValues: (dimensionType: Dimension['type']) => string[],
): boolean => {
  const node = calculateGetNodeById(nodeId, nodes);
  const nodeCanHaveChildren =
    node && (node.id === ROOT_NODE_ID || node.type === 'baseNode');
  if (!nodeCanHaveChildren) {
    return false;
  }
  const childFirstNode = calculateChildNodes(node.id, nodes, edges)[0];
  if (!childFirstNode) {
    return false;
  }
  const childNodesDimension =
    childFirstNode.type === 'baseNode' && childFirstNode.data.dimension?.type;
  if (!childNodesDimension) {
    return false;
  }
  const allPossibleDimensionValues = getDimensionValues(childNodesDimension);
  const selectedDimensionValues = getChildrenValues(
    nodeId,
    nodes,
    edges,
  ).filter((value) => value !== null);
  const hasAllOtherSelected = getChildrenValues(nodeId, nodes, edges).includes(
    null,
  );

  return (
    hasAllOtherSelected &&
    selectedDimensionValues.length === allPossibleDimensionValues.length
  );
};
